import { useEffect, useState } from "react"
import { Modal, Button, Form, Spinner } from "react-bootstrap"
import FiltersGrid from "../../_filters-grid/filters-grid"
import { PublisherOrCampaign, IFilterSelectorOpts, IFilterSelectorOpts_Selected } from "../../home/dashboard-content/types"
import { IAlertType, ICustomAlert } from "./custom-alerts"


type Props = {
    show: boolean
    onClose: () => void
    onSave: (alert: Omit<ICustomAlert, 'id'|'created'> & {id?: string, created?: string} ) => Promise<void>
    onRemove(id: string): Promise<void>
    existingAlert?: ICustomAlert
    info: IFilterSelectorOpts
}

const comparisonOptions = [
    { label: "Previous 3 days", value: "3d" },
    { label: "Previous 7 days", value: "7d" },
    { label: "Previous 14 days", value: "14d" },
    { label: "Previous 30 days", value: "30d" },
]

function isSameItem(a: any, b: any): boolean {
    if (typeof a === 'string' && typeof b === 'string') return a === b
    return a.id === b.id && a.network === b.network && a.country === b.country
}

function isAllSelected<T>(
    selected: T[],
    all: T[],
    compareFn: (a: T, b: T) => boolean
): boolean {
    if (selected.length !== all.length) return false
    return all.every(a => selected.some(b => compareFn(a, b)))
}

export default function CustomAlertModal({ show, onClose, onSave, existingAlert, info, onRemove }: Props) {
    const [name, setName] = useState("")
    const [metric, setMetric] = useState<"revenue" | "clicks">("revenue")
    const [direction, setDirection] = useState<"increase" | "decrease">("decrease")
    const [percentage, setPercentage] = useState<number | "">("")
    const [comparisonPeriod, setComparisonPeriod] = useState("7d")
    const [type] = useState<IAlertType>("perfomance_change")
    const [isSaving, setIsSaving] = useState(false)
    const [isDisabled, setIsDisabled] = useState(false)



    const [filters, setFilters] = useState<IFilterSelectorOpts>({
        countries: info.countries || [],
        networks: info.networks || [],
        publishers: info.publishers || [],
        campaigns: info.campaigns || [],
        channels: []
    })

    useEffect(() => {
        if (existingAlert) {
            setName(existingAlert.name)
            setMetric(existingAlert.metric)
            setDirection(existingAlert.direction)
            setPercentage(existingAlert.percentage)
            setComparisonPeriod(existingAlert.comparisonPeriod)
            setIsDisabled(existingAlert.disabled ?? false)
            if (existingAlert.filters) {
                const campaigns = existingAlert.filters.campaigns === true ? info.campaigns : existingAlert.filters.campaigns
                const publishers = existingAlert.filters.publishers === true ? info.publishers : existingAlert.filters.publishers
                const networks = existingAlert.filters.networks === true ? info.networks : existingAlert.filters.networks
                const countries = existingAlert.filters.countries === true ? info.countries : existingAlert.filters.countries
                setFilters({campaigns, publishers, networks, countries, channels: []})
            }
        } else {
            setName("")
            setMetric("revenue")
            setDirection("decrease")
            setPercentage("")
            setComparisonPeriod("7d")
            setIsDisabled(false)
            setFilters({
                countries: info.countries || [],
                networks: info.networks || [],
                publishers: info.publishers || [],
                campaigns: info.campaigns || [],
                channels: []
            })
        }
    }, [existingAlert, show, info])

    const handleRemove = async () => {
        try {
            setIsSaving(true)
            
            if (existingAlert) {
                await onRemove(existingAlert.id)
                onClose()
            }
        } finally {
            setIsSaving(false)
        }
    }

    const handleSave = async () => {
        if (!name.trim()) {
            alert("Please provide a name for the alert.")
            return
        }

        if (!percentage || percentage <= 0) {
            alert("Percentage must be greater than 0.")
            return
        }
        setIsSaving(true)
        try {
            const allCountriesSelected = isAllSelected(filters.countries, info.countries, (a, b) => a === b)
            const allNetworksSelected = isAllSelected(filters.networks, info.networks, (a, b) => a === b)
            const allCampaignsSelected = isAllSelected(filters.campaigns, info.campaigns, isSameItem)
            const allPublishersSelected = isAllSelected(filters.publishers, info.publishers, isSameItem)
    
            const newFilters: IFilterSelectorOpts_Selected = {
                campaigns: allCampaignsSelected ? true : filters.campaigns,
                countries: allCountriesSelected ? true : filters.countries,
                networks: allNetworksSelected ? true : filters.networks,
                publishers: allPublishersSelected ? true : filters.publishers,
            }
    
    
            await onSave({
                id: existingAlert?.id,
                name,
                type,
                metric,
                direction,
                percentage,
                comparisonPeriod,
                filters: newFilters,
                disabled: isDisabled,
            })

            onClose()
        } finally {
            setIsSaving(false)
        }
    }

    function areAllItemsSelected(current: any[], all: any[]) {
        return current.length === all.length &&
            all.every(item =>
                current.some(i => isSameItem(i, item))
            )
    }

    const handleSelectAll = (
        filterKey: keyof typeof filters,
        options: any[]
    ) => {
        setFilters((prev) => ({
            ...prev,
            [filterKey]: areAllItemsSelected(prev[filterKey], options)
                ? []
                : [...options],
        }))
    }

    return (
        <Modal show={show} onHide={onClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>{existingAlert ? "Edit Custom Alert" : "Create Custom Alert"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <div className="row">
                        <div className="col-12 col-md-6 mb-3">
                            <Form.Group controlId="type">
                                <Form.Label>Type</Form.Label>
                                <Form.Select value={type} disabled>
                                <option value="perfomance_change">Performance change</option>
                                </Form.Select>
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-6 mb-3">
                            <Form.Group controlId="alertName">
                                <Form.Label>Alert Name</Form.Label>
                                <Form.Control
                                type="text"
                                placeholder="e.g. Weekly revenue drop"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                />
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-6 mb-3">
                            <Form.Group controlId="metric">
                                <Form.Label>Metric</Form.Label>
                                <Form.Select
                                value={metric}
                                onChange={(e) => setMetric(e.target.value as "revenue" | "clicks")}
                                >
                                <option value="revenue">Revenue</option>
                                <option value="clicks">Clicks</option>
                                </Form.Select>
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-6 mb-3">
                            <Form.Group controlId="direction">
                                <Form.Label>Direction</Form.Label>
                                <Form.Select
                                value={direction}
                                onChange={(e) => setDirection(e.target.value as "increase" | "decrease")}
                                >
                                <option value="increase">Increase</option>
                                <option value="decrease">Decrease</option>
                                </Form.Select>
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-6 mb-3">
                            <Form.Group controlId="percentage">
                                <Form.Label>Percentage Threshold</Form.Label>
                                <Form.Control
                                type="number"
                                value={percentage}
                                onChange={(e) => {
                                    const value = e.target.value
                                    setPercentage(value === "" ? "" : Number(value))
                                }}
                                min={1}
                                step={1}
                                />
                            </Form.Group>
                        </div>

                        <div className="col-12 col-md-6 mb-3">
                            <Form.Group controlId="comparisonPeriod">
                                <Form.Label>Comparison Period</Form.Label>
                                <Form.Select
                                value={comparisonPeriod}
                                onChange={(e) => setComparisonPeriod(e.target.value)}
                                >
                                {comparisonOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                                </Form.Select>
                                <Form.Text muted>
                                This alert will run daily and compare data from the selected period to the previous matching period.
                                </Form.Text>
                            </Form.Group>
                        </div>
                    </div>
                    </Form>

                <div className="mt-4">
                    <Form.Label>Filters</Form.Label>
                    <FiltersGrid
                        allCollapsed={true}
                        opts={info}
                        selectedFilters={filters}
                        onItemSelect={(type, item) => {
                            setFilters((prev) => {
                                const current = prev[type]
                                const isSelected =
                                    typeof item === 'string'
                                        ? (current as string[]).includes(item)
                                        : (current as PublisherOrCampaign[]).some(
                                            (i) =>
                                                i.id === item.id &&
                                                i.network === item.network &&
                                                i.country === item.country
                                        )

                                const updated = isSelected
                                    ? (current as any[]).filter((i) =>
                                        typeof item === 'string'
                                            ? i !== item
                                            : i.id !== item.id || i.network !== item.network || i.country !== item.country
                                    )
                                    : [...current, item]

                                return {
                                    ...prev,
                                    [type]: updated,
                                }
                            })
                        }}
                        onSelectAll={handleSelectAll}
                    />
                </div>
                <div className="col-12 mt-3">
                    <Form.Check>
                        <Form.Check.Input
                        type="checkbox"
                        id="disable-alert-checkbox"
                        checked={isDisabled}
                        onChange={(e) => setIsDisabled(e.target.checked)}
                        />
                        <Form.Check.Label htmlFor="disable-alert-checkbox">
                        Disable this alert
                        </Form.Check.Label>
                    </Form.Check>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button className={`me-auto ${existingAlert ? "" : " d-none"}` } variant="danger" onClick={handleRemove} disabled={isSaving}>Delete</Button>
                <Button variant="secondary" onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? (
                        <>
                            <span className="me-2">Saving</span>
                            <Spinner as="span" animation="border" size="sm" />
                        </>
                    ) : (
                        existingAlert ? "Save Changes" : "Create Alert"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
